import { Badge } from '@/shared/ui/badge';
import { Smartphone } from 'lucide-react';

interface NoTasksBadgeProps {
  /** True when the job has at least one live task (job_list.has_tasks). */
  hasTasks: boolean | undefined;
  /** True when the job has a live schedule row (job_list.is_scheduled). */
  isScheduled: boolean | undefined;
}

/**
 * "Not on mobile" badge for a job row. Renders only for a job that has been
 * scheduled but has no tasks — the one state in which the job is invisible to
 * every engineer's device.
 *
 * Scheduling fans out one job_planned_task row per task × team member
 * (plannerService.createEvent) and inserts nothing when the job has no tasks;
 * the mobile "my jobs" query INNER JOINs job_planned_task, so a task-less
 * scheduled job silently disappears rather than erroring. Adding a task later
 * back-fills the planned rows for every active plan (addJobTask, WRK-5738), so
 * this badge clears itself once the job is fixed.
 *
 * Unscheduled jobs are deliberately not flagged: they aren't expected on mobile
 * yet, so warning about them would be noise (WRK-5792).
 */
export function NoTasksBadge({ hasTasks, isScheduled }: NoTasksBadgeProps) {
  if (!isScheduled) return null;
  if (hasTasks !== false) return null;

  return (
    <Badge className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/30 hover:bg-amber-500/20 gap-1 text-nowrap">
      <Smartphone className="size-3 shrink-0" />
      Not on mobile
    </Badge>
  );
}
